import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Copy, Loader2, AlignLeft, ArrowLeft, Play } from "lucide-react";
import { toast } from "sonner";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { runSummary } from "@/components/research-hub/labels";

type Run = {
  id: string;
  action: string;
  model: string;
  cost_usd: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  response_excerpt: string | null;
  payload_ref: any;
  created_at: string;
  completed_at: string | null;
  critique_applied: boolean | null;
  status: string;
  item_id: string | null;
};

const ROLE_META: Record<string, { label: string; sub: string; defaultOpen?: boolean }> = {
  tier1_compare_referee: { label: "Referee verdict", sub: "Final adjudication", defaultOpen: true },
  tier1_compare_bull: { label: "Bull case", sub: "Argues FOR alignment" },
  tier1_compare_bear: { label: "Bear case", sub: "Argues AGAINST alignment / for drift" },
  tier1_compare: { label: "Initial compare draft", sub: "Pre-debate baseline" },
};
const ROLE_ORDER = ["tier1_compare_referee", "tier1_compare_bull", "tier1_compare_bear", "tier1_compare"];

const POLARITY_POS = ["supports", "bullish", "confirms", "aligned", "consistent", "endorses"];
const POLARITY_NEG = ["contradicts", "bearish", "refutes", "drift", "inconsistent", "rejects"];

function fmtUSD(n?: number | null) {
  return `$${(n ?? 0).toFixed(4)}`;
}
function fmtNum(n?: number | null) {
  return (n ?? 0).toLocaleString("en-GB");
}
function fmtDateTime(s?: string | null) {
  if (!s) return "—";
  const d = new Date(s);
  return d.toLocaleString("en-GB");
}
function extractFullOutput(run: Run): string {
  const pr = run.payload_ref;
  if (pr && typeof pr === "object") {
    const candidates = [pr.output, pr.output_full, pr.text, pr.response, pr.completion];
    for (const c of candidates) if (typeof c === "string" && c.length) return c;
  }
  return run.response_excerpt ?? "";
}
function extractCitations(text: string): string[] {
  const out = new Set<string>();
  const re = /\[(?:source|item|ref):([^\]]+)\]/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) out.add(m[0]);
  return [...out];
}
function extractVerdict(refereeText: string): { label: string; tone: "default" | "secondary" | "destructive" | "outline" } {
  const upper = refereeText.toUpperCase();
  if (/\bESCALATE\b/.test(upper)) return { label: "ESCALATE", tone: "destructive" };
  if (/\bDRIFT\b/.test(upper)) return { label: "DRIFT", tone: "destructive" };
  if (/\bALIGN(ED|MENT)?\b/.test(upper)) return { label: "ALIGN", tone: "default" };
  return { label: "PENDING", tone: "secondary" };
}

function findConflicts(bullText: string, bearText: string) {
  const bullCites = new Set(extractCitations(bullText));
  const bearCites = new Set(extractCitations(bearText));
  const shared = [...bullCites].filter((c) => bearCites.has(c));
  const conflicts: { citation: string; bull: string; bear: string }[] = [];
  const sentSplit = (s: string) => s.split(/(?<=[\.!?])\s+/);
  for (const cite of shared) {
    const bullSent = sentSplit(bullText).find((s) => s.includes(cite) && POLARITY_POS.some((p) => s.toLowerCase().includes(p)));
    const bearSent = sentSplit(bearText).find((s) => s.includes(cite) && POLARITY_NEG.some((p) => s.toLowerCase().includes(p)));
    if (bullSent && bearSent) conflicts.push({ citation: cite, bull: bullSent.trim(), bear: bearSent.trim() });
  }
  return conflicts;
}

function copyId(id: string) {
  navigator.clipboard.writeText(id);
  toast.success("Run id copied");
}

export default function ResearchDebateTrace() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { enabled: flagOn, isLoading: flagLoading } = useFeatureFlag("phase_8_debate_viewer");
  const [triggering, setTriggering] = useState(false);

  const itemQ = useQuery({
    queryKey: ["research_item_min", id],
    enabled: !!id && flagOn,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_items")
        .select("id, title")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const runsQ = useQuery({
    queryKey: ["research_debate_runs", id],
    enabled: !!id && flagOn,
    refetchInterval: triggering ? 4000 : false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_ai_call_runs")
        .select("id, action, model, cost_usd, input_tokens, output_tokens, response_excerpt, payload_ref, created_at, completed_at, critique_applied, status, item_id")
        .eq("item_id", id!)
        .in("action", ROLE_ORDER)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      // Pick the most recent run per action
      const byAction = new Map<string, Run>();
      for (const r of (data ?? []) as Run[]) {
        if (!byAction.has(r.action)) byAction.set(r.action, r);
      }
      return byAction;
    },
  });

  // Auto-stop polling when referee row arrives
  useEffect(() => {
    if (triggering && runsQ.data?.has("tier1_compare_referee")) {
      setTriggering(false);
      toast.success("Debate trace ready");
    }
  }, [triggering, runsQ.data]);

  async function triggerAlignment() {
    if (!id) return;
    setTriggering(true);
    const { error } = await supabase.functions.invoke("research-agent-tier1-alignment", { body: { item_id: id } });
    if (error) {
      setTriggering(false);
      toast.error(error.message || "Failed to start tier1 alignment");
      return;
    }
    toast.message("Tier 1 alignment started — polling for results…");
    qc.invalidateQueries({ queryKey: ["research_debate_runs", id] });
  }

  if (flagLoading) {
    return <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>;
  }
  if (!flagOn) {
    return (
      <div className="p-8 max-w-xl mx-auto text-center">
        <h2 className="text-lg font-semibold mb-2">Debate trace viewer</h2>
        <p className="text-sm text-muted-foreground">
          This area is gated by the <code className="px-1 bg-muted rounded">phase_8_debate_viewer</code> feature flag and is currently disabled.
        </p>
      </div>
    );
  }

  const runs = runsQ.data;
  const referee = runs?.get("tier1_compare_referee");
  const bull = runs?.get("tier1_compare_bull");
  const bear = runs?.get("tier1_compare_bear");
  const compare = runs?.get("tier1_compare");
  const allRuns = [referee, bull, bear, compare].filter(Boolean) as Run[];

  const totalCost = allRuns.reduce((s, r) => s + (r.cost_usd ?? 0), 0);
  const verdict = referee ? extractVerdict(extractFullOutput(referee)) : null;

  const citations = useMemo(() => {
    const set = new Set<string>();
    for (const r of allRuns) extractCitations(extractFullOutput(r)).forEach((c) => set.add(c));
    return [...set];
  }, [allRuns]);

  const conflicts = useMemo(() => {
    if (!bull || !bear) return [];
    return findConflicts(extractFullOutput(bull), extractFullOutput(bear));
  }, [bull, bear]);

  return (
    <TooltipProvider>
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button asChild variant="ghost" size="sm">
            <Link to={`/partner/research/${id}`}><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to item</Link>
          </Button>
        </div>

        <header className="space-y-2">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold">
              {itemQ.data?.title ?? "Research item"} <span className="text-muted-foreground font-normal">· Debate trace</span>
            </h1>
            {verdict && (
              <Badge variant={verdict.tone} className="text-sm px-3 py-1">{verdict.label}</Badge>
            )}
          </div>
          {referee ? (
            <p className="text-sm text-muted-foreground">
              {fmtDateTime(referee.completed_at ?? referee.created_at)} · {referee.model} · total {fmtUSD(totalCost)} across {allRuns.length} role{allRuns.length === 1 ? "" : "s"}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">No referee run yet.</p>
          )}
        </header>

        {runsQ.isLoading && (
          <div className="text-center text-muted-foreground py-12">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading debate runs…
          </div>
        )}

        {!runsQ.isLoading && allRuns.length === 0 && (
          <Card>
            <CardContent className="p-10 text-center space-y-4">
              <AlignLeft className="h-8 w-8 mx-auto text-muted-foreground" />
              <div>
                <h3 className="font-semibold">No debate trace for this item</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Trigger tier 1 alignment to generate the bull/bear/referee debate.
                </p>
              </div>
              <Button onClick={triggerAlignment} disabled={triggering}>
                {triggering ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running…</> : <><Play className="h-4 w-4 mr-2" />Trigger tier 1 alignment</>}
              </Button>
            </CardContent>
          </Card>
        )}

        {!runsQ.isLoading && allRuns.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
            <div>
              <Accordion type="multiple" defaultValue={["tier1_compare_referee"]}>
                {ROLE_ORDER.map((action) => {
                  const run = runs?.get(action);
                  if (!run) return null;
                  const meta = ROLE_META[action];
                  const full = extractFullOutput(run);
                  return (
                    <AccordionItem key={action} value={action}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex flex-1 items-center justify-between pr-4">
                          <div className="text-left">
                            <div className="font-semibold">{meta.label}</div>
                            <div className="text-xs text-muted-foreground">{meta.sub} · {run.model}</div>
                          </div>
                          <div className="text-xs text-muted-foreground hidden sm:block">
                            {fmtUSD(run.cost_usd)} · {fmtNum(run.input_tokens)} in / {fmtNum(run.output_tokens)} out
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3">
                          {action === "tier1_compare_referee" && (
                            <div className="text-xs">
                              <Badge variant={run.critique_applied ? "default" : "outline"}>
                                self_critique_applied: {String(!!run.critique_applied)}
                              </Badge>
                            </div>
                          )}
                          <pre className="text-sm font-mono whitespace-pre-wrap bg-muted/40 rounded p-4 border max-h-[600px] overflow-auto">
                            {full || "(no output captured)"}
                          </pre>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            <span>Cost: <strong>{fmtUSD(run.cost_usd)}</strong></span>
                            <span>Tokens: <strong>{fmtNum(run.input_tokens)}</strong> in / <strong>{fmtNum(run.output_tokens)}</strong> out</span>
                            <button
                              onClick={() => copyId(run.id)}
                              className="inline-flex items-center gap-1 hover:text-foreground"
                              title="Copy run id to clipboard"
                            >
                              <Copy className="h-3 w-3" /> {runSummary(run)}
                            </button>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>

            <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
              <Card>
                <CardContent className="p-4 space-y-2">
                  <h3 className="text-sm font-semibold">Citations</h3>
                  {citations.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No citations detected.</p>
                  ) : (
                    <ul className="text-xs font-mono space-y-1 max-h-64 overflow-auto">
                      {citations.map((c) => <li key={c} className="truncate">{c}</li>)}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">Conflict map</h3>
                    <Tooltip>
                      <TooltipTrigger className="text-xs text-muted-foreground underline decoration-dotted">heuristic</TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs">
                        Flags shared citations where bull uses a positive polarity verb (supports, bullish…) and bear uses a negative one (contradicts, bearish…). Approximate.
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  {conflicts.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No direct contradictions found.</p>
                  ) : (
                    <ul className="space-y-3">
                      {conflicts.map((c, i) => (
                        <li key={i} className="text-xs space-y-1 border-l-2 border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-2 rounded-r">
                          <div className="font-mono text-[10px] text-muted-foreground">{c.citation}</div>
                          <div><span className="font-semibold text-emerald-700 dark:text-emerald-400">Bull:</span> {c.bull}</div>
                          <div><span className="font-semibold text-rose-700 dark:text-rose-400">Bear:</span> {c.bear}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </aside>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
